import dayjs, { Dayjs } from 'dayjs';
import { array, date, object, string } from 'yup';

import { LotteryType } from 'v2/constants';
import { LotterySettingsBgImages } from 'v2/types/LotteryModel';

export interface LotteryPrizeForm {
  name: string;
  description: string;
}

export type LotterySettingsForm = {
  shortDescription: string;
  fullDescription: string;
  casinoId: string;
  type: LotteryType;
  isActive: boolean;
  startDate: Dayjs | null;
  endDate: Dayjs;
  prizePlacesCount: string;
  prizes: LotteryPrizeForm[];
} & LotterySettingsBgImages;

const PRIZE_VISIBLE_CHARACTERS_LIMIT = 28;

type IntlSegmenter = new (
  locales?: Intl.LocalesArgument,
  options?: { granularity: 'grapheme' }
) => {
  segment: (value: string) => Iterable<unknown>;
};

export const LOTTERY_PRIZE_STUB: LotteryPrizeForm = {
  name: '',
  description: '',
};

export const initialValues: LotterySettingsForm = {
  shortDescription: '',
  fullDescription: '',
  casinoId: '',
  type: LotteryType.USER_ID,
  isActive: false,
  startDate: dayjs(),
  endDate: dayjs(),
  prizePlacesCount: '',
  prizes: [],
  desktopImage: '',
  tabletImage: '',
  mobileImage: '',
};

export const normalizePrizeText = (value: string) => value.replace(/&nbsp;/g, '\u00A0');

const intlWithSegmenter = Intl as typeof Intl & { Segmenter?: IntlSegmenter };

const visibleCharacterSplitter = intlWithSegmenter.Segmenter
  ? new intlWithSegmenter.Segmenter(undefined, {
      granularity: 'grapheme',
    })
  : null;

export const countVisibleCharacters = (value: string) => {
  const normalizedValue = normalizePrizeText(value);

  if (visibleCharacterSplitter) {
    return Array.from(visibleCharacterSplitter.segment(normalizedValue)).length;
  }

  return Array.from(normalizedValue).length;
};

const prizeTextSchema = string()
  .required('Required')
  .test('not-blank', 'Required', (value) => {
    if (!value) return false;
    return normalizePrizeText(value).trim().length > 0;
  })
  .test(
    'visible-characters-limit',
    `Must not contain more than ${PRIZE_VISIBLE_CHARACTERS_LIMIT} visible characters`,
    (value) => (value ? countVisibleCharacters(value) <= PRIZE_VISIBLE_CHARACTERS_LIMIT : true)
  );

export const validationSchema = object().shape({
  startDate: date().nullable(),
  endDate: date()
    .required('End Date is required')
    .test('is-after-start-date', 'End Date must be later than Start Date', function (value) {
      const { startDate } = this.parent as LotterySettingsForm;

      if (!value || !startDate) return true;

      return dayjs(value).isAfter(dayjs(startDate));
    }),
  prizePlacesCount: string()
    .optional()
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .test(
      'min-0-or-empty',
      'Prize Places Count must be equal or greater than 0',
      (value) => !value || (/^\d+$/.test(value) && Number(value) >= 0)
    ),
  prizes: array().of(
    object().shape({
      name: prizeTextSchema,
      description: prizeTextSchema,
    })
  ),
});
